import { create } from 'zustand';
import {
  clampThreadPanelWidth,
  loadThreadPanelMode,
  loadThreadPanelWidth,
  saveThreadPanelMode,
  saveThreadPanelWidth,
  type ThreadPanelMode,
} from '../lib/threadPanelPlacement';

/**
 * The thread panel's placement, shared by the two surfaces that need it: the
 * shell decides where to put the panel, and the panel's own header renders the
 * toggle that flips it. Neither owns the other, so the preference lives beside
 * them instead of being threaded through the shell as props.
 */
interface SessionPanelLayout {
  owner: string;
  width: number;
  expanded: boolean;
}

interface ThreadPanelState {
  mode: ThreadPanelMode;
  /** Total column width in CSS pixels, drag handle included. */
  width: number;

  /** Ephemeral layout of the currently visible Task Session. */
  sessionLayout: SessionPanelLayout | null;
  setSessionView: (owner: string, active: boolean) => void;
  toggleSessionExpanded: (owner: string) => void;
  toggleMode: () => void;
  setWidth: (width: number) => void;
  /**
   * Writes the current width to storage. A drag reports a width per pointer
   * move and moves the state on every one of them; the storage write is worth a
   * synchronous round trip once, when the edge comes to rest, and not sixty
   * times a second while it travels.
   */
  persistWidth: () => void;
}

export const useThreadPanelStore = create<ThreadPanelState>()((set, get) => ({
  mode: loadThreadPanelMode(),
  width: loadThreadPanelWidth(),
  sessionLayout: null,

  setSessionView: (owner, active) => set(state => {
    if (!active) return state.sessionLayout?.owner === owner ? { sessionLayout: null } : state;
    return state.sessionLayout?.owner === owner ? state : {
      sessionLayout: { owner, width: 640, expanded: false },
    };
  }),

  toggleSessionExpanded: (owner) => set(state => state.sessionLayout?.owner === owner ? {
    sessionLayout: { ...state.sessionLayout, expanded: !state.sessionLayout.expanded },
  } : state),

  toggleMode: () => {
    const mode: ThreadPanelMode = get().mode === 'pinned' ? 'float' : 'pinned';
    saveThreadPanelMode(mode);
    set({ mode });
  },

  setWidth: (width) => set(state => state.sessionLayout
    ? { sessionLayout: { ...state.sessionLayout, width: clampThreadPanelWidth(width) } }
    : { width: clampThreadPanelWidth(width) }),

  persistWidth: () => {
    if (!get().sessionLayout) saveThreadPanelWidth(get().width);
  },
}));
